import { useContext, useEffect, useMemo, useState } from 'react'

import {
  PWGCardEmbed,
  PWGCardEmbedProps,
  PWGForm,
  PWGFormProps,
  PWGFormValue,
  useHealthToolsCache,
} from '@hhgtech/hhg-components'
import {
  LOCALE_HAS_COMMUNITY_SITE,
  QUERY_HEALTH_TOOL,
} from '@hhgtech/hhg-components/constants'
import { getURLwithSSOTracking } from '@hhgtech/hhg-components/ssoV2'

import { callApiNew } from 'api/axios'
import { formatPWGContent } from 'api/dataTransform'
import { PATHS } from 'api/paths'
import { redirectToSSOWithReturnUrl } from 'api/utils/redirects'
import { useCurrentUrl } from 'hooks/useCurrentUrl'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { basePath } from 'interfaces/constants'
import { HEALTH_TOOL_TYPE } from 'interfaces/constants/healthTools'
import { PregnancyWeightLocalStoredType, PwgInfo } from 'interfaces/types'
import { AppContext } from 'state/appContext'

import { HealthToolCardEmbedProps } from '../types'

type CommonProps = HealthToolCardEmbedProps

export type PWGCardWrapperProps =
  | ({ onlyUseForm: true } & Partial<PWGFormProps> & CommonProps)
  | ({ onlyUseForm?: false } & Partial<PWGCardEmbedProps> & CommonProps)

export const PWGCardWrapper = ({
  isSide,
  onlyUseForm,
  ...props
}: PWGCardWrapperProps) => {
  const { locale, router } = useRouterExtend()

  const { state } = useContext(AppContext) || {}

  const {
    isLoggedIn, //healthInfo
  } = state || {}
  const [pwgContent, setPwgContent] = useState<PwgInfo>({} as PwgInfo)

  const { getHealthToolLink } = useHealthToolsCache(locale)
  const toolLink = useMemo(() => getHealthToolLink(HEALTH_TOOL_TYPE.PWG), [
    getHealthToolLink,
  ])

  useEffect(() => {
    ;(async () => {
      const res = await callApiNew(PATHS.HEALTH_TOOL.GET_HEALTH_TOOL_PWG_INFO)
      setPwgContent(formatPWGContent(res?.data))
    })()
  }, [])

  const onSubmit = (values: PWGFormValue) => {
    const weight = Number(values.weight)
    const height = Number(values.height)
    const lastWeight = Number(values.lastWeight)
    const pregnantWeek = Number(values.pregnantWeek)
    const bmi =
      Math.round((weight / (((height / 100) * height) / 100)) * 10) / 10
    const weightGain = weight - lastWeight
    const type = values.hasTwin ? 'twins' : 'notwins'
    const pwgInfo: PregnancyWeightLocalStoredType = {
      yourSelf: 'self',
      pwg: weightGain,
      bmi: bmi,
      week: Number(values.pregnantWeek),
      twin: Boolean(values.hasTwin),
      weightBefore: lastWeight,
      weight,
      height,
      source_url: window.location.href,
    }
    localStorage.setItem('pwgInfo', JSON.stringify(pwgInfo))
    ;(async () => {
      // get result

      // add result to Sidis

      const redirectPageUrl = toolLink

      const destination = `${
        typeof window !== 'undefined' ? window.location.origin : ''
      }${basePath}${redirectPageUrl}?${QUERY_HEALTH_TOOL.pwg}=${weightGain}&${
        QUERY_HEALTH_TOOL.bmi
      }=${bmi}&${QUERY_HEALTH_TOOL.week}=${pregnantWeek}&${
        QUERY_HEALTH_TOOL.twins
      }=${type}&${QUERY_HEALTH_TOOL.weightBefore}=${lastWeight}&${
        QUERY_HEALTH_TOOL.weight
      }=${weight}${
        LOCALE_HAS_COMMUNITY_SITE.includes(locale) ? '&joincommunity=1' : ''
      }`

      if (!isLoggedIn) {
        redirectToSSOWithReturnUrl(destination, 'pregnancy-weight-gain')
      } else {
        router.push(
          getURLwithSSOTracking(destination, {
            source: 'pregnancy-weight-gain',
          })?.href || destination,
        )
      }
    })()
  }
  const isMobile = useIsMobile()
  const currentUrl = useCurrentUrl()

  return toolLink ? (
    onlyUseForm ? (
      <PWGForm
        formLayout={isMobile || isSide ? 'default' : 'inline'}
        onSubmit={onSubmit}
        submitProps={{
          dataEventLabel: currentUrl,
        }}
        {...props}
      />
    ) : (
      <PWGCardEmbed
        pwgContent={pwgContent}
        formProps={
          {
            formLayout: isMobile || isSide ? 'default' : 'inline',
            onSubmit,
            submitProps: {
              dataEventLabel: currentUrl,
            },
          } as any
        }
        {...props}
      />
    )
  ) : null
}
